import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { apiErrorMessage } from '@/api/client'
import { orders as ordersApi } from '@/api/endpoints'
import { Alert, Field, Input, Modal, Spinner, Textarea } from '@/components/ui'
import { currencySymbol, formatAmount } from '@/lib/utils'
import type { InvoiceCustomer } from '@/types/api'

const EMPTY: InvoiceCustomer = {
  customer_name: '',
  customer_street: '',
  customer_city: '',
  customer_postal_code: '',
  customer_country: '',
  customer_ico: '',
  customer_dic: '',
  customer_vat_id: '',
}

/**
 * „Vystaviť faktúru“ k objednávke - z detailov platby, popri potvrdení
 * o platbe. Odberateľ sa predvyplní z poslednej faktúry klienta; položky,
 * DPH a úhrada sú dané objednávkou (`GET /orders/<vs>/invoice`).
 */
export default function InvoiceDialog({ paymentRef, onClose }: { paymentRef: number; onClose: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const symbol = currencySymbol()
  const draft = useQuery({
    queryKey: ['invoice-draft', paymentRef],
    queryFn: () => ordersApi.invoiceDraft(paymentRef),
  })
  const [customer, setCustomer] = useState<InvoiceCustomer>(EMPTY)
  const [issueDate, setIssueDate] = useState('')
  const [supplyDate, setSupplyDate] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!draft.data) return
    setCustomer(draft.data.customer)
    setIssueDate(draft.data.issue_date)
    setSupplyDate(draft.data.supply_date)
  }, [draft.data])

  const issue = useMutation({
    mutationFn: () =>
      ordersApi.issueInvoice(paymentRef, {
        ...customer,
        issue_date: issueDate || undefined,
        supply_date: supplyDate || undefined,
        note,
      }),
    onSuccess: (invoice) => {
      void qc.invalidateQueries({ queryKey: ['order'] })
      void qc.invalidateQueries({ queryKey: ['invoice-draft'] })
      window.open(ordersApi.invoicePdfUrl(invoice.id), '_blank', 'noopener')
      onClose()
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const data = draft.data
  const blocked = Boolean(data && (data.supplier_missing.length || data.vat_problem || data.existing))
  const paid = Number.parseFloat(data?.paid ?? '0')
  const total = Number.parseFloat(data?.total ?? '0')
  const set = (name: keyof InvoiceCustomer) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setCustomer((current) => ({ ...current, [name]: e.target.value }))

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={data ? t('invoice.titleNumber', { number: data.next_number }) : t('invoice.title')}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('global.cancel')}
          </button>
          <button
            type="submit"
            form="issue-invoice"
            className="btn-primary"
            disabled={!data || blocked || !customer.customer_name.trim() || issue.isPending}
          >
            {issue.isPending ? <Spinner /> : null}
            {t('invoice.issue')}
          </button>
        </>
      }
    >
      {!data ? (
        <div className="flex h-32 items-center justify-center text-slate-400">
          <Spinner className="h-5 w-5" />
        </div>
      ) : (
        <form
          id="issue-invoice"
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (!blocked) issue.mutate()
          }}
        >
          {error ? <Alert variant="error">{error}</Alert> : null}
          {data.supplier_missing.length ? (
            <Alert variant="warning">
              {t('invoice.supplierMissing')}{' '}
              <Link to="/settings/billing" className="font-medium underline">
                {t('invoice.supplierLink')}
              </Link>
            </Alert>
          ) : null}
          {data.vat_problem ? <Alert variant="warning">{t('invoice.vatRateMissing')}</Alert> : null}
          {data.existing ? (
            <Alert variant="info">{t('invoice.exists', { number: data.existing.number })}</Alert>
          ) : null}

          <div>
            <h3 className="mb-1.5 text-xs font-semibold uppercase text-slate-500">{t('invoice.customer')}</h3>
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label={t('invoice.customerName')} className="sm:col-span-2">
                <Input required autoFocus value={customer.customer_name} onChange={set('customer_name')} />
              </Field>
              <Field label={t('invoice.street')} className="sm:col-span-2">
                <Input value={customer.customer_street} onChange={set('customer_street')} />
              </Field>
              <Field label={t('invoice.postalCode')}>
                <Input value={customer.customer_postal_code} onChange={set('customer_postal_code')} />
              </Field>
              <Field label={t('invoice.city')}>
                <Input value={customer.customer_city} onChange={set('customer_city')} />
              </Field>
              <Field label={t('invoice.country')}>
                <Input value={customer.customer_country} onChange={set('customer_country')} />
              </Field>
              <Field label={t('invoice.ico')}>
                <Input value={customer.customer_ico} onChange={set('customer_ico')} />
              </Field>
              <Field label={t('invoice.dic')}>
                <Input value={customer.customer_dic} onChange={set('customer_dic')} />
              </Field>
              <Field label={t('invoice.vatId')}>
                <Input value={customer.customer_vat_id} onChange={set('customer_vat_id')} />
              </Field>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <Field label={t('invoice.issueDate')}>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </Field>
            <Field label={t('invoice.supplyDate')}>
              <Input type="date" value={supplyDate} onChange={(e) => setSupplyDate(e.target.value)} />
            </Field>
          </div>
          <Field label={t('invoice.note')}>
            <Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>

          <dl className="space-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-600">{t('invoice.items', { count: data.items.length })}</dt>
              <dd className="font-semibold tabular-nums">
                {formatAmount(data.total)} {symbol}
              </dd>
            </div>
            {data.vat_payer && data.vat_rate !== null ? (
              <div className="flex justify-between text-slate-600">
                <dt>{t('invoice.vatLine', { rate: Number.parseFloat(data.vat_rate) })}</dt>
                <dd className="tabular-nums">
                  {formatAmount(data.base)} + {formatAmount(data.vat)} {symbol}
                </dd>
              </div>
            ) : (
              <div className="text-xs text-slate-500">{t('invoice.noVat')}</div>
            )}
            <div className="flex justify-between text-slate-600">
              <dt>{paid >= total ? t('invoice.paidFull') : t('invoice.paidPart', { days: data.due_days })}</dt>
              <dd className="tabular-nums">
                {formatAmount(data.paid)} {symbol}
              </dd>
            </div>
          </dl>
        </form>
      )}
    </Modal>
  )
}
